/**
 * Pheme message sanitizer — the gate that decides whether a
 * server-driven message is safe to render in the final user-facing UI.
 *
 * Why this lives in the client even though Pheme already moderates content:
 *
 *   - Pheme is the only writer of these payloads but mistakes still slip
 *     through (incident 2026-05-08 carried `DEBUG —` titles into prod and
 *     the `{delta_vs_prev_month_pct}%` template was rendered raw on the
 *     Historico header for ~2 hours).
 *   - The cuidari-app surfaces are the last line of defence before the
 *     user sees the string. A pure-function gate keeps the rules
 *     auditable and unit-testable independent of the network layer.
 *
 * Drop rules (PR feat/m0-phase0-stabilize-prod):
 *
 *   1. Wrong placement: `placement_slot` does not match the slot the hook
 *      is rendering. Pheme has had cross-placement bleed when admins
 *      reuse a draft from another slot.
 *   2. Unresolved template: title/body/cta still contain `{token}` style
 *      placeholders that the server failed to fill.
 *   3. Non-prod copy: any visible string contains `debug`, `demo`,
 *      `placeholder`, or `teste` — those words are reserved for staging
 *      and indicate the payload was published to the wrong environment.
 *   4. Internal platform names: any visible string mentions an internal
 *      service (`hephaestus`, `pheme`, `cerberus`, …). Users do not need
 *      to know the cockpit's vocabulary.
 *   5. Stale time window: the message is gated by `starts_at` /
 *      `expires_at` and the current wall clock falls outside that window.
 *      Pheme schedules promos this way; we must not show a "expired" or
 *      "not yet active" promo to a user whose tab was kept open.
 *
 * Discarded messages are logged via `console.warn` with the rule code so
 * cockpit operators can grep the browser console during incident
 * triage, and surfaced once-per-id via the optional `track` hook so the
 * RUM stream records the drop without flooding (`useMessages` plugs the
 * analytics tracker in at render time).
 */

import type { PlacementSlot, ServerMessage } from './types'

export type DropReason =
  | 'wrong_placement'
  | 'unresolved_template'
  | 'non_prod_copy'
  | 'internal_platform_name'
  | 'time_window_invalid'
  | 'suppressed_feature'

export interface DroppedMessage {
  message_id: string
  placement_slot: string
  expected_placement: PlacementSlot
  reason: DropReason
}

interface SanitizeContext {
  /** Wall clock for the time-window gate. Defaults to `Date.now()`. */
  now?: () => Date
  /** Hook called once per dropped message — used by the hook to emit RUM. */
  onDrop?: (dropped: DroppedMessage) => void
}

const UNRESOLVED_TEMPLATE_RE = /\{[a-zA-Z0-9_.-]+(?:\|[^}]*)?\}/
const NON_PROD_MESSAGE_RE = /(^|\s|[-_])(debug|demo|placeholder|teste)(\s|[-_]|$)/i
const INTERNAL_PLATFORM_RE =
  /\b(hephaestus|cerberus|peitho|pheme|hestia|janus|kratos|mnemosyne|eunomia|aegis|clio)\b/i
// Suppress the bogus "PIX recorrente identificado" reco-story (B05): Pheme has
// been emitting a PIX recurring-detection story on `home.reco_stories` that is
// not a real feature yet. Drop it by message_id pattern or visible PIX copy.
const SUPPRESSED_RECO_ID_RE = /reco_stories\.pix|pix_alert/i
const SUPPRESSED_RECO_COPY_RE = /\bpix\b/i

function messageText(m: ServerMessage): string {
  return [
    m.message_id,
    m.title,
    m.subtitle,
    m.headline,
    m.body,
    m.cta_label,
    m.content_ref,
    m.decision_reason_code,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
}

function parseTimestamp(value: string | undefined): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Returns the first drop reason that fires, or `null` if the message is
 * safe to render. Order matches the rule numbers in the file header so
 * that the `console.warn` output is predictable when an operator is
 * triaging a stuck placement.
 */
export function classifyMessage(
  m: ServerMessage,
  placement: PlacementSlot,
  ctx: SanitizeContext = {},
): DropReason | null {
  if (m.placement_slot !== placement) return 'wrong_placement'
  if (
    placement === 'home.reco_stories' &&
    (SUPPRESSED_RECO_ID_RE.test(m.message_id) || SUPPRESSED_RECO_COPY_RE.test(messageText(m)))
  ) {
    return 'suppressed_feature'
  }
  const text = messageText(m)
  if (UNRESOLVED_TEMPLATE_RE.test(text)) return 'unresolved_template'
  if (NON_PROD_MESSAGE_RE.test(text)) return 'non_prod_copy'
  if (INTERNAL_PLATFORM_RE.test(text)) return 'internal_platform_name'

  const now = (ctx.now ?? (() => new Date()))().getTime()
  const startsAt = parseTimestamp(m.starts_at)
  const expiresAt = parseTimestamp(m.expires_at)
  if (startsAt !== null && now < startsAt) return 'time_window_invalid'
  if (expiresAt !== null && now >= expiresAt) return 'time_window_invalid'

  return null
}

/**
 * Filter the message list down to renderable entries. Side effects:
 *
 *   - `console.warn` once per dropped message (`[pheme] dropped <id>
 *     reason=<…>`) — cheap on the hot path and lets devs see why a
 *     placement is silent during local testing.
 *   - `ctx.onDrop` invoked per drop. The hook layer wires this to the
 *     analytics tracker so the RUM stream gets one event per discarded
 *     message_id (the hook deduplicates).
 */
export function sanitizeMessages(
  messages: readonly ServerMessage[],
  placement: PlacementSlot,
  ctx: SanitizeContext = {},
): ServerMessage[] {
  const safe: ServerMessage[] = []
  for (const m of messages) {
    const reason = classifyMessage(m, placement, ctx)
    if (reason === null) {
      safe.push(m)
      continue
    }
    console.warn(
      `[pheme] dropped message ${m.message_id} placement=${placement} reason=${reason}`,
    )
    ctx.onDrop?.({
      message_id: m.message_id,
      placement_slot: m.placement_slot,
      expected_placement: placement,
      reason,
    })
  }
  return safe
}
